import React, { useState, useMemo, useEffect } from 'react';
import { MapPin, Users, Clock, Repeat, Route } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './dialog';
import { apiService } from '../../services/apiService';
import { toast } from 'sonner';
import RunCard from './RunCard';
import ScrollableCardModal from './ScrollableCardModal';
import { convertHeicToWebFormat, getPrimaryImageUrl, getAllImageUrls, getDisplayImageUrl, getImageLoadingProps } from '../../utils/imageUtils';
import { backendUrl } from '../../config/environment';
import { UI } from '../../constants/designTokens';
import { Carousel, CarouselContent, CarouselItem } from './carousel';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceFromUser } from '../../utils/distanceUtils';

/** Format event type for display (e.g. "social_run" → "Social run"). */
function formatEventTypeLabel(type) {
  if (!type || typeof type !== 'string') return '';
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase().replace(/_/g, ' ');
}

const MeetingCard = ({
  event,
  onClick,
  showHover = true,
  showLocation = true,
  buttonText = "View Run",
  onButtonClick,
  additionalButtons = null,
  showPrimaryButton = true,
  /** When true, use loading="eager" and fetchpriority="high" for the card image (e.g. visible carousel slide). */
  priority = false,
  /** When true, use card-sized image URL for list/homepage (faster load). */
  useCardSize = false,
}) => {
  const { user } = useAuth();
  const displayUnit = user?.distance_unit || 'miles';
  const [showParticipants, setShowParticipants] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [showRunCard, setShowRunCard] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [loadingRunCard, setLoadingRunCard] = useState(false);
  const [showRunModal, setShowRunModal] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  /** When true, card variant 404ed; use full-size URL (backward compat for existing images). */
  const [cardUrlFailed, setCardUrlFailed] = useState(false);
  const [fetchedClubName, setFetchedClubName] = useState(null);
  const [carouselApi, setCarouselApi] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const displayClubName = event.club_name || fetchedClubName;

  useEffect(() => {
    if (!event.club_id || event.club_name) {
      setFetchedClubName(null);
      return;
    }
    let cancelled = false;
    apiService.clubs.getById(event.club_id)
      .then((club) => {
        if (!cancelled && club?.name) setFetchedClubName(club.name);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [event.club_id, event.club_name]);

  const DESCRIPTION_CHAR_LIMIT = 150;
  const DEFAULT_DESCRIPTION = '';

  // Derive image URL like mobile: prefer event.images array, then event.image, then null (placeholder)
  const displayImageUrl = useMemo(() => {
    const fallback = event.image || null;
    const url = getPrimaryImageUrl(event.images, fallback);
    if (!url) return null;
    const webUrl = convertHeicToWebFormat(url);
    // Make relative URLs absolute (e.g. /uploads/...) so the browser loads from the backend
    if (webUrl && webUrl.startsWith('/')) {
      const base = backendUrl.replace(/\/$/, '');
      return `${base}${webUrl}`;
    }
    return webUrl;
  }, [event.images, event.image]);

  const imageUrl = useMemo(() => {
    const base = imageSrc || displayImageUrl;
    if (!base) return null;
    const useCard = useCardSize && !cardUrlFailed;
    return getDisplayImageUrl(base, useCard);
  }, [displayImageUrl, imageSrc, useCardSize, cardUrlFailed]);

  // All image URLs for carousel (absolute URLs for relative paths, HEIC already converted by getAllImageUrls)
  const imageUrls = useMemo(() => {
    const raw = event.images && Array.isArray(event.images) && event.images.length > 0
      ? getAllImageUrls(event.images)
      : (event.image ? [convertHeicToWebFormat(event.image)] : []);
    const base = backendUrl.replace(/\/$/, '');
    return raw.map((url) => (url && url.startsWith('/') ? `${base}${url}` : url)).filter(Boolean);
  }, [event.images, event.image]);

  // Memoized description logic with error handling
  const descriptionLogic = useMemo(() => {
    try {
      const description = event?.description || DEFAULT_DESCRIPTION;
      const shouldTruncate = description.length > DESCRIPTION_CHAR_LIMIT;
      const displayText = shouldTruncate && !isDescriptionExpanded 
        ? description.substring(0, DESCRIPTION_CHAR_LIMIT) + '...'
        : description;
      
      return {
        description,
        shouldTruncate,
        displayText
      };
    } catch (error) {
      console.error('Error processing description:', error);
      return {
        description: DEFAULT_DESCRIPTION,
        shouldTruncate: false,
        displayText: DEFAULT_DESCRIPTION
      };
    }
  }, [event?.description, isDescriptionExpanded]);

  const handleClick = () => {
    if (onClick) {
      onClick(event);
    }
  };

  const handleButtonClick = (e) => {
    e.stopPropagation();
    if (onButtonClick) {
      onButtonClick(event);
    } else {
      // Open meeting modal
      setShowRunModal(true);
    }
  };

  const handleAttendeesClick = async (e) => {
    e.stopPropagation();
    
    if (!event.club_id) {
      toast.error('Meeting information not available');
      return;
    }
    
    // Use the event ID as the meeting ID
    const meetingId = event.id;
    
    setShowParticipants(true);
    setLoadingParticipants(true);
    
    try {
      const response = await apiService.clubs.getRunParticipants(event.club_id, meetingId);
      setParticipants(response);
    } catch (error) {
      console.error('Failed to fetch participants:', error);
      toast.error('Failed to load participants');
      setParticipants([]);
    } finally {
      setLoadingParticipants(false);
    }
  };

  const handleParticipantClick = async (participant) => {
    if (participant.user_id) {
      setLoadingRunCard(true);
      setSelectedParticipant(participant);
      setShowRunCard(true);
      
      try {
        // Fetch the user's full profile data
        const userProfile = await apiService.users.getProfile(participant.user_id);
        setSelectedParticipant(userProfile);
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
        toast.error('Failed to load run card');
        setShowRunCard(false);
      } finally {
        setLoadingRunCard(false);
      }
    } else {
      console.error('No user_id found in participant data');
      toast.error('Unable to view participant profile');
    }
  };

  const handleBackToParticipants = () => {
    setShowRunCard(false);
    setSelectedParticipant(null);
  };

  return (
    <div
      className={`rounded-3xl overflow-hidden relative bg-runcards-background ${
        showHover ? 'hover:scale-105 transition-transform duration-300' : ''
      } ${onClick ? 'cursor-pointer' : ''}`}
      onClick={handleClick}
    >
      {/* Image Section - Carousel when multiple images, single image otherwise */}
      <div className="relative h-[260px] sm:h-[310px] overflow-hidden rounded-t-3xl rounded-b-3xl">
        {imageUrls.length > 1 ? (
          <div
            className="w-full h-full relative"
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerMove={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
          >
            <Carousel
              opts={{
                align: 'start',
                loop: true,
              }}
              className="w-full h-full"
              setApi={(api) => {
                if (api) {
                  setCarouselApi(api);
                  setCurrentImageIndex(api.selectedScrollSnap());
                  api.on('select', () => setCurrentImageIndex(api.selectedScrollSnap()));
                }
              }}
            >
              <CarouselContent className="h-full -ml-0">
                {imageUrls.map((url, index) => {
                  const displayUrl = getDisplayImageUrl(url, useCardSize && !cardUrlFailed);
                  return (
                    <CarouselItem key={index} className="h-full pl-0 basis-full">
                      <div className="relative w-full h-full">
                        <img
                          src={displayUrl}
                          alt={`${event.name} - Image ${index + 1}`}
                          className="w-full h-full object-cover cursor-pointer"
                          {...(index === 0 ? getImageLoadingProps(priority) : {})}
                          style={{
                            objectPosition: event.imagePosition ? `${event.imagePosition.x}% ${event.imagePosition.y}%` : 'center center'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleButtonClick(e);
                          }}
                          onError={(e) => {
                            const currentSrc = e.target.src;
                            if (useCardSize && currentSrc.includes('_card.jpg')) {
                              setCardUrlFailed(true);
                              return;
                            }
                            setImageError(true);
                            e.target.style.display = 'none';
                          }}
                          onLoad={() => setImageError(false)}
                        />
                      </div>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <div role="group" aria-label="Image carousel" onClick={(e) => e.stopPropagation()} className="absolute inset-0 pointer-events-none">
                <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 z-10 pointer-events-auto">
                  {imageUrls.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      aria-label={`Image ${index + 1} of ${imageUrls.length}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        carouselApi?.scrollTo(index);
                      }}
                      className={`rounded-full transition-all duration-200 ${
                        currentImageIndex === index
                          ? 'w-2 h-2 bg-white shadow'
                          : 'w-1.5 h-1.5 bg-white/60 hover:bg-white/80'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </Carousel>
          </div>
        ) : imageUrl && !imageError ? (
          <img
            src={imageUrl}
            alt={event.name}
            className="w-full h-full object-cover cursor-pointer"
            {...getImageLoadingProps(priority)}
            style={{
              objectPosition: event.imagePosition ? `${event.imagePosition.x}% ${event.imagePosition.y}%` : 'center center'
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleButtonClick(e);
            }}
            onError={(e) => {
              const currentSrc = e.target.src;
              if (useCardSize && currentSrc.includes('_card.jpg')) {
                setCardUrlFailed(true);
                return;
              }
              if (!imageSrc && event.image && event.image.includes('.heic')) {
                const convertedUrl = convertHeicToWebFormat(event.image);
                if (convertedUrl !== currentSrc) {
                  setImageSrc(convertedUrl);
                  return;
                }
              }
              if (!currentSrc.includes('.jpg') || !event.image?.includes('.heic')) {
                console.error('Failed to load event image:', currentSrc);
              }
              setImageError(true);
            }}
            onLoad={() => {
              setImageError(false);
            }}
          />
        ) : null}
        <div
          className="w-full h-full bg-runcards-background spray-paint-placeholder"
          style={{ display: (imageUrls.length > 1 || imageUrl) && !imageError ? 'none' : 'flex' }}
        />

        {/* Recurring Badge - Top Right */}
        {event.is_recurring && event.recurrence_pattern && event.recurrence_pattern !== 'weekly' && (
          <div className="absolute top-16 right-4">
            <span className="bg-blue-600/60 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-medium border border-white/20 flex items-center gap-1">
              <Repeat className="w-3 h-3" />
              {event.recurrence_pattern === 'daily' ? 'Daily' : 
               event.recurrence_pattern}
            </span>
          </div>
        )}
      </div>

      {/* Information Section - Dark background like mobile */}
      <div className="bg-runcards-background rounded-b-3xl">
        {/* Basic Info Section */}
        <div className="px-4 pt-2 pb-4">
          {/* Event Title */}
          <h3 className="text-lg font-runcards text-runcards-text-primary mb-2">
            {event.name}
          </h3>

          {event.reliability_index?.eligible && event.reliability_index?.byline ? (
            <p className="text-[13px] font-normal mb-2" style={{ color: UI.COLORS.RELIABILITY_BYLINE }}>
              {event.reliability_index.byline}
            </p>
          ) : null}

          {/* Run Notes */}
          {descriptionLogic.displayText ? (
            <>
              <p className="text-runcards-text-secondary text-sm mb-3 leading-relaxed">
                {descriptionLogic.displayText}
              </p>
              {descriptionLogic.shouldTruncate && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDescriptionExpanded(!isDescriptionExpanded);
                  }}
                  className="text-runcards-accent text-sm font-semibold hover:text-runcards-text-primary transition-colors"
                >
                  {isDescriptionExpanded ? 'View Less' : 'View More'}
                </button>
              )}
            </>
          ) : null}

          {/* Powered by {run club} - same as mobile RunScreen */}
          {displayClubName && (
            <p className="text-runcards-text-secondary text-base -mt-2 mb-2">
              Powered by{' '}
              <span className="text-runcards-text-primary font-medium">
                {displayClubName}
              </span>
            </p>
          )}

          {/* Event Details - Day, date, time (same as mobile RunScreen) */}
          <div className="flex flex-col space-y-2 mb-4">
            {(event.dateWithDayLabel || event.time) && (
              <div className="flex items-center text-runcards-text-primary">
                <Clock className="w-4 h-4 mr-2 text-runcards-text-secondary flex-shrink-0" />
                <span className="text-sm">
                  {event.dateWithDayLabel && event.dateWithDayLabel !== 'TBD' ? (
                    <>
                      {event.dateWithDayLabel}
                      {event.time ? ` • ${event.time}` : ''}
                    </>
                  ) : event.time ? (
                    event.time
                  ) : (
                    'TBD'
                  )}
                </span>
              </div>
            )}
            {showLocation && (event.location || event.city) && (
              <div className="flex items-center text-runcards-text-primary">
                <MapPin className="w-4 h-4 mr-2 text-runcards-text-secondary flex-shrink-0" />
                <span className="text-sm">
                  {event.location || event.city}
                </span>
              </div>
            )}
            {(event.distanceFromUser != null || event.distance_from_user != null) && (
              <div className="flex items-center text-runcards-text-primary">
                <Route className="w-4 h-4 mr-2 text-runcards-text-secondary flex-shrink-0" />
                <span className="text-sm">
                  {formatDistanceFromUser(Number(event.distanceFromUser ?? event.distance_from_user), displayUnit)} away
                  {event.type && ` • ${formatEventTypeLabel(event.type)}`}
                </span>
              </div>
            )}
          </div>
          
          {/* Action buttons */}
          {(showPrimaryButton || additionalButtons) && (
            <div className="flex justify-center gap-2">
              {showPrimaryButton && (
                <button
                  type="button"
                  className="bg-runcards-accent text-runcards-white px-6 py-2 rounded-full font-semibold text-sm hover:bg-runcards-accent-hover transition-colors shadow-lg focus:outline-none focus:ring-2 focus:ring-runcards-accent"
                  onClick={handleButtonClick}
                >
                  {buttonText}
                </button>
              )}
              {additionalButtons}
            </div>
          )}
        </div>
      </div>

      {/* Participants Modal */}
      <Dialog open={showParticipants} onOpenChange={(open) => {
        setShowParticipants(open);
        if (!open) {
          setShowRunCard(false);
        }
      }}>
        <DialogContent className="bg-black/90 backdrop-blur-xl border border-white/20 max-w-md mx-auto shadow-2xl">
          {!showRunCard ? (
            // Participants List View
            <>
              <DialogHeader className="pb-4">
                <DialogTitle className="text-white flex items-center text-xl font-bold" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                  <Users className="w-6 h-6 mr-3" />
                  Participants ({participants.length})
                </DialogTitle>
                <DialogDescription className="text-white/70 text-sm mt-1" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  People registered for this run
                </DialogDescription>
              </DialogHeader>
              
              <div className="max-h-80 overflow-y-auto">
                {loadingParticipants ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-white/60" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      Loading participants...
                    </div>
                  </div>
                ) : participants.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-white/60" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      No participants yet
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {participants.map((participant, index) => {
                      return (
                      <div key={participant.user_id ?? `participant-${index}`} className="flex items-center space-x-4 p-5 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                        <div className="w-16 h-16 bg-white/10 border border-white/20 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {participant.user_profile_picture ? (
                            <img 
                              src={participant.user_profile_picture} 
                              alt={participant.name}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            <Users className="w-8 h-8 text-white/60" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <button
                            onClick={() => handleParticipantClick(participant)}
                            className="text-white font-semibold hover:text-blue-300 transition-colors duration-200 text-left w-full text-base"
                            style={{fontFamily: 'Montserrat, sans-serif'}}
                          >
                            {participant.name || 'Unknown User'}
                          </button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : (
            // Run Card View
            <>
              {/* Hidden accessibility elements */}
              <DialogHeader className="sr-only">
                <DialogTitle>Run Card</DialogTitle>
                <DialogDescription>
                  {selectedParticipant?.name ? `Run card for ${selectedParticipant.name}` : 'Loading run card'}
                </DialogDescription>
              </DialogHeader>
              
              <div className="relative">
                <div className="flex justify-center">
                  {loadingRunCard ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-white/60" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        Loading run card...
                      </div>
                    </div>
                  ) : selectedParticipant ? (
                    <RunCard 
                      runner={selectedParticipant}
                      showHover={false}
                      buttonText="View Profile"
                      onButtonClick={() => {
                        toast.info('Profile details coming soon');
                      }}
                    />
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-white/60" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        Unable to load run card
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Meeting Modal */}
      <ScrollableCardModal
        cardType="meeting"
        cardId={event.id}
        isOpen={showRunModal}
        onClose={() => setShowRunModal(false)}
      />
    </div>
  );
};

export default MeetingCard;

